/**
 * ForgotPassword — the customer recovery-request view (F14 FR2/FR3).
 *
 * Reached from the login screen's "Forgot password?" action; needs no session.
 * Takes an email address, checks only its SYNTAX client-side, and posts it to
 * `POST /auth/forgot-password`.
 *
 * The single most important property of this screen is what it does NOT do: it
 * shows the same confirmation whatever the answer, because the backend returns
 * the byte-identical 202 for an eligible account, an unknown address, an
 * inactive or unverified account, and one inside the cooldown window (FR4). A
 * screen that said "we sent it" for one and "no account found" for another would
 * hand an attacker the account-enumeration oracle the whole endpoint is shaped
 * to deny. So the copy asserts only what we actually know ("if an account
 * matches"), never that a mail was sent.
 *
 * Chrome matches LoginScreen (masthead + bordered card, Tailwind tokens) rather
 * than the older inline-style RegisterScreen/VerifyEmail: this screen is entered
 * from the login screen and returns to it, so it is the one the user sees either
 * side of this step.
 */

import { useState } from "react";
import * as api from "../engine/api";
import { isValidEmail } from "../engine/emailValidation";
import useResendCooldown from "../hooks/useResendCooldown";

/**
 * The one confirmation, shown for every 202. Held as a module constant so a test
 * can assert the SAME string is rendered whichever address was typed, which is
 * the only mechanical way to pin a non-disclosure property from the outside: a
 * per-branch literal could drift apart one edit at a time without any assertion
 * noticing.
 */
export const RESET_REQUEST_NOTICE =
  "If an account matches that email address, a password reset link is on its way. " +
  "Check your inbox, and look in your spam folder if it does not arrive.";

export default function ForgotPassword({ onBackToLogin }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Mirrors the backend's per-account `password_reset_cooldown_secs` (60s), the
  // same reasoning as the L01 resend button: the server silently suppresses a
  // repeat request inside the window, so a button that keeps inviting clicks is
  // promising something that will not happen.
  const { remaining, onCooldown, start: startCooldown } = useResendCooldown();

  const submit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (submitting || onCooldown) return;
    setError("");

    // Syntax only (FR3). The backend's EmailStr is authoritative and this is a
    // pre-flight guard, exactly as on the registration form.
    if (!isValidEmail(email)) {
      setError("Enter a valid email address");
      return;
    }

    setSubmitting(true);
    try {
      await api.forgotPassword(email);
      // 202 for every outcome. There is nothing in the response to branch on and
      // we must not try to infer anything from it.
      setNotice(RESET_REQUEST_NOTICE);
      startCooldown();
    } catch (err) {
      // Only transport, throttling (429) or a degraded backend (503) land here;
      // account state never does. Surface the envelope's readable sentence.
      setError(err.message || "Could not request a password reset. Try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="bg-ink rounded-none px-7 pt-7 pb-6">
          <div className="text-white font-semibold text-2xl tracking-[0.22em]">OMEGA AI HPC</div>
          <div className="text-ink-muted text-sm mt-1">Reset your password</div>
        </div>

        {/* noValidate: the input keeps type="email" for the right mobile keyboard
            and semantics, but native constraint validation is switched off so a
            malformed address surfaces in THIS form's error box rather than a
            browser bubble styled by nobody. It also keeps isValidEmail the single
            client-side gate, matching RegisterScreen (which has no <form> at all,
            so never had a native layer to compete with). */}
        <form
          data-testid="forgot-password-form"
          onSubmit={submit}
          noValidate
          className="bg-surface border border-line border-t-0 rounded-none px-7 py-6"
        >
          <p className="text-sm text-fg2 mb-4 leading-relaxed">
            Enter the email address on your account and we will send you a link to choose a new
            password.
          </p>

          <label htmlFor="forgot-email" className="block text-xs text-fg2 uppercase tracking-wide mb-1">
            Email
          </label>
          <input
            id="forgot-email"
            type="email"
            value={email}
            onChange={(ev) => {
              setEmail(ev.target.value);
              setError("");
            }}
            autoComplete="email"
            className="w-full px-3 py-2 mb-4 rounded-none border border-line bg-surface text-fg text-sm outline-none focus:border-accent-fill"
          />

          {notice && (
            <div
              data-testid="forgot-notice"
              role="status"
              className="text-sm text-fg2 bg-surface2 border border-line rounded-none px-3 py-2 mb-4 leading-relaxed"
            >
              {notice}
            </div>
          )}

          {error && (
            <div
              data-testid="forgot-error"
              className="text-sm text-danger bg-danger-tint border border-danger/20 rounded-none px-3 py-2 mb-4"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="forgot-submit"
            disabled={submitting || onCooldown}
            aria-busy={submitting}
            className="w-full py-2.5 rounded-none bg-accent-fill text-white text-sm font-medium cursor-pointer hover:bg-accent-strong transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting
              ? "Sending..."
              : onCooldown
                ? `Try again in ${remaining}s`
                : "Send reset link"}
          </button>

          <button
            type="button"
            onClick={onBackToLogin}
            data-testid="forgot-back"
            className="w-full py-2 mt-2 text-sm text-accent cursor-pointer hover:text-accent-strong bg-transparent border-0"
          >
            Back to login
          </button>
        </form>
      </div>
    </div>
  );
}
